import { useCallback, useEffect, useState } from 'react'
import {
  Button,
  Chip,
  DateRangePicker,
  Progress,
  RangeValue,
  Spinner,
  Tab,
  Tabs
} from '@nextui-org/react'
import { CalendarDate, DateValue } from '@internationalized/date'
import { InitialsAvatar } from '~/components/common/InitialsAvatar'
import { Modal, ModalBody, ModalFooter, useModal } from '~/components/modals/CurieModal'
import { Typography } from '~/components/common/Typography'
import { BetterToast } from '~/utils/toast'
import {
  getUserDetails,
  getUserTasks,
  getUserAccessLogs,
  revokeUserSessions,
  resendVerificationEmail,
  setUserEmailVerified,
  setUserMfaEnabled,
  setUserPhoneVerified,
  unlockUserAccountByAdmin,
  UserDetailsResponse,
  UserTasksResponse,
  UserAccessLog
} from '~/services/superAdminService'

// Icons
const CopyIcon = () => (
  <svg className='w-4 h-4' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
    <path
      strokeLinecap='round'
      strokeLinejoin='round'
      strokeWidth={2}
      d='M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z'
    />
  </svg>
)

const CheckCircleIcon = () => (
  <svg className='w-4 h-4' fill='currentColor' viewBox='0 0 20 20'>
    <path
      fillRule='evenodd'
      d='M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z'
      clipRule='evenodd'
    />
  </svg>
)

const ExclamationCircleIcon = () => (
  <svg className='w-4 h-4' fill='currentColor' viewBox='0 0 20 20'>
    <path
      fillRule='evenodd'
      d='M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z'
      clipRule='evenodd'
    />
  </svg>
)

const ShieldCheckIcon = () => (
  <svg className='w-4 h-4' fill='currentColor' viewBox='0 0 20 20'>
    <path
      fillRule='evenodd'
      d='M2.166 4.999A11.954 11.954 0 0010 1.944 11.954 11.954 0 0017.834 5c.11.65.166 1.32.166 2.001 0 5.225-3.34 9.67-8 11.317C5.34 16.67 2 12.225 2 7c0-.682.057-1.35.166-2.001zm11.541 3.708a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z'
      clipRule='evenodd'
    />
  </svg>
)

const ACCOUNT_LOCK_THRESHOLD = 5

interface UserDetailsProps {
  userId: number
  isOpen: boolean
  onClose: () => void
  onUpdate?: () => void
}

const UserDetails = ({ userId, isOpen, onClose, onUpdate }: UserDetailsProps) => {
  const [details, setDetails] = useState<UserDetailsResponse | null>(null)
  const [tasks, setTasks] = useState<UserTasksResponse | null>(null)
  const [accessLogs, setAccessLogs] = useState<UserAccessLog[] | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [isLoadingTasks, setIsLoadingTasks] = useState(false)
  const [isLoadingLogs, setIsLoadingLogs] = useState(false)
  const [activeTab, setActiveTab] = useState('overview')
  const [actionLoading, setActionLoading] = useState<string | null>(null)

  const getDefaultDateRange = (): RangeValue<DateValue> => {
    const now = new Date()
    const sevenDaysAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000)
    return {
      start: new CalendarDate(
        sevenDaysAgo.getFullYear(),
        sevenDaysAgo.getMonth() + 1,
        sevenDaysAgo.getDate()
      ),
      end: new CalendarDate(now.getFullYear(), now.getMonth() + 1, now.getDate())
    }
  }

  const [logDateRange, setLogDateRange] = useState<RangeValue<DateValue>>(getDefaultDateRange())

  const confirmModal = useModal()
  const [confirmAction, setConfirmAction] = useState<{
    title: string
    message: string
    action: () => Promise<void>
  } | null>(null)

  const fetchDetails = useCallback(async () => {
    if (!userId) return
    try {
      setIsLoading(true)
      const data = await getUserDetails(userId)
      setDetails(data)
    } catch (error) {
      console.error('Failed to fetch user details:', error)
      BetterToast.error('Failed to load user details')
    } finally {
      setIsLoading(false)
    }
  }, [userId])

  const fetchTasks = useCallback(async () => {
    if (!userId) return
    try {
      setIsLoadingTasks(true)
      const data = await getUserTasks(userId, 50)
      setTasks(data)
    } catch (error) {
      console.error('Failed to fetch user tasks:', error)
    } finally {
      setIsLoadingTasks(false)
    }
  }, [userId])

  const fetchAccessLogs = useCallback(async () => {
    if (!details?.profile.email) return
    try {
      setIsLoadingLogs(true)
      const startDate = new Date(
        Date.UTC(logDateRange.start.year, logDateRange.start.month - 1, logDateRange.start.day)
      ).toISOString()
      const endDate = new Date(
        Date.UTC(
          logDateRange.end.year,
          logDateRange.end.month - 1,
          logDateRange.end.day,
          23,
          59,
          59,
          999
        )
      ).toISOString()
      const logs = await getUserAccessLogs(details.profile.email, startDate, endDate)
      setAccessLogs(logs)
    } catch (error) {
      console.error('Failed to fetch access logs:', error)
      BetterToast.error('Failed to load access logs')
      setAccessLogs([])
    } finally {
      setIsLoadingLogs(false)
    }
  }, [details?.profile.email, logDateRange])

  useEffect(() => {
    if (isOpen && userId) {
      fetchDetails()
      setTasks(null)
      setAccessLogs(null)
      setActiveTab('overview')
    }
  }, [isOpen, userId, fetchDetails])

  useEffect(() => {
    if (activeTab === 'tasks' && !tasks && !isLoadingTasks) {
      fetchTasks()
    }
  }, [activeTab, tasks, isLoadingTasks, fetchTasks])

  useEffect(() => {
    if (activeTab === 'access-logs' && accessLogs === null && !isLoadingLogs && details) {
      fetchAccessLogs()
    }
  }, [activeTab, accessLogs, isLoadingLogs, details, fetchAccessLogs])

  const handleRevokeSessions = async () => {
    setConfirmAction({
      title: 'Revoke All Sessions',
      message: `This will log out ${details?.profile.email} from all devices. Are you sure?`,
      action: async () => {
        setActionLoading('revoke')
        try {
          const result = await revokeUserSessions(userId)
          BetterToast.success(result.message)
          fetchDetails()
          onUpdate?.()
        } catch (error) {
          BetterToast.error((error as Error).message)
        } finally {
          setActionLoading(null)
        }
      }
    })
    confirmModal.open()
  }

  const handleResendVerification = async () => {
    setActionLoading('resend')
    try {
      const result = await resendVerificationEmail(userId)
      BetterToast.success(result.message)
    } catch (error) {
      BetterToast.error((error as Error).message)
    } finally {
      setActionLoading(null)
    }
  }

  const handleToggleEmailVerified = async () => {
    if (!details) return
    const newValue = !details.verification.email_verified
    setActionLoading('email')
    try {
      await setUserEmailVerified(userId, newValue)
      BetterToast.success(`Email ${newValue ? 'verified' : 'unverified'} successfully`)
      fetchDetails()
      onUpdate?.()
    } catch (error) {
      BetterToast.error((error as Error).message)
    } finally {
      setActionLoading(null)
    }
  }

  const handleTogglePhoneVerified = async () => {
    if (!details) return
    const newValue = !details.verification.phone_verified
    setActionLoading('phone')
    try {
      await setUserPhoneVerified(userId, newValue)
      BetterToast.success(`Phone ${newValue ? 'verified' : 'unverified'} successfully`)
      fetchDetails()
      onUpdate?.()
    } catch (error) {
      BetterToast.error((error as Error).message)
    } finally {
      setActionLoading(null)
    }
  }

  const handleToggleMfa = async () => {
    if (!details) return
    const newValue = !details.verification.mfa_enabled
    setConfirmAction({
      title: newValue ? 'Enable MFA' : 'Disable MFA',
      message: newValue
        ? `Enable MFA for ${details.profile.email}?`
        : `Disable MFA for ${details.profile.email}? This may reduce account security.`,
      action: async () => {
        setActionLoading('mfa')
        try {
          await setUserMfaEnabled(userId, newValue)
          BetterToast.success(`MFA ${newValue ? 'enabled' : 'disabled'} successfully`)
          fetchDetails()
          onUpdate?.()
        } catch (error) {
          BetterToast.error((error as Error).message)
        } finally {
          setActionLoading(null)
        }
      }
    })
    confirmModal.open()
  }

  const handleUnlockAccount = async () => {
    setConfirmAction({
      title: 'Unlock Account',
      message: `This will reset the failed login attempts for ${details?.profile.email} and unlock their account. Are you sure?`,
      action: async () => {
        setActionLoading('unlock')
        try {
          const result = await unlockUserAccountByAdmin(userId)
          BetterToast.success(result.message)
          fetchDetails()
          onUpdate?.()
        } catch (error) {
          BetterToast.error((error as Error).message)
        } finally {
          setActionLoading(null)
        }
      }
    })
    confirmModal.open()
  }

  const executeConfirmAction = async () => {
    if (confirmAction) {
      await confirmAction.action()
      confirmModal.close()
      setConfirmAction(null)
    }
  }

  const handleDateRangeChange = (value: RangeValue<DateValue> | null) => {
    if (value) {
      setLogDateRange(value)
      setAccessLogs(null)
    }
  }

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text)
    BetterToast.success('Copied to clipboard')
  }

  if (!isOpen) return null

  return (
    <>
      <Modal
        isOpen={isOpen}
        onClose={onClose}
        hideCloseButton={false}
        className='!max-w-[1100px] w-[1100px]'
      >
        <ModalBody className='p-0 h-[75vh]'>
          {isLoading ? (
            <div className='flex items-center justify-center h-full'>
              <Spinner size='lg' />
            </div>
          ) : details ? (
            <div className='flex h-full'>
              {/* Left Sidebar - User Profile */}
              <div className='w-60 flex-shrink-0 bg-tokens-neutral-bg-grey-1 border-r border-tokens-neutral-border-2 p-5 flex flex-col overflow-y-auto'>
                {/* Avatar */}
                <div className='flex flex-col items-center text-center mb-5'>
                  <div className='mb-3'>
                    <InitialsAvatar
                      name={details.profile.name || details.profile.email || '?'}
                      size='lg'
                    />
                  </div>
                  <Typography
                    fontFamily='geist'
                    fontSize='titleSm'
                    fontWeight='semibold'
                    className='text-tokens-neutral-text-primary'
                  >
                    {details.profile.name || 'No Name'}
                  </Typography>
                  <Typography
                    fontFamily='geistMono'
                    fontSize='labelSm'
                    className='text-tokens-neutral-text-tertiary mt-0.5'
                  >
                    @{details.profile.username}
                  </Typography>
                </div>

                {/* Status Badges */}
                <div className='flex flex-wrap gap-1.5 justify-center mb-5'>
                  {details.verification.email_verified ? (
                    <Chip
                      color='success'
                      variant='flat'
                      size='sm'
                      startContent={<CheckCircleIcon />}
                    >
                      Verified
                    </Chip>
                  ) : (
                    <Chip
                      color='warning'
                      variant='flat'
                      size='sm'
                      startContent={<ExclamationCircleIcon />}
                    >
                      Unverified
                    </Chip>
                  )}
                  {details.verification.mfa_enabled && (
                    <Chip
                      color='primary'
                      variant='flat'
                      size='sm'
                      startContent={<ShieldCheckIcon />}
                    >
                      MFA
                    </Chip>
                  )}
                  {details.verification.unsuccessful_login_attempts > ACCOUNT_LOCK_THRESHOLD && (
                    <Chip
                      color='danger'
                      variant='flat'
                      size='sm'
                      startContent={<ExclamationCircleIcon />}
                    >
                      Locked
                    </Chip>
                  )}
                </div>

                {/* User Info */}
                <div className='space-y-3 flex-1'>
                  <div>
                    <Typography
                      fontFamily='geist'
                      fontSize='labelSm'
                      className='text-tokens-neutral-text-tertiary uppercase tracking-wider mb-0.5'
                    >
                      Email
                    </Typography>
                    <div className='flex items-center gap-1.5'>
                      <Typography
                        fontFamily='geist'
                        fontSize='labelSm'
                        className='text-tokens-neutral-text-primary truncate flex-1'
                      >
                        {details.profile.email}
                      </Typography>
                      <button
                        onClick={() => copyToClipboard(details.profile.email)}
                        className='text-tokens-neutral-text-tertiary hover:text-tokens-neutral-text-secondary transition-colors flex-shrink-0'
                        title='Copy email'
                      >
                        <CopyIcon />
                      </button>
                    </div>
                  </div>

                  <div>
                    <Typography
                      fontFamily='geist'
                      fontSize='labelSm'
                      className='text-tokens-neutral-text-tertiary uppercase tracking-wider mb-0.5'
                    >
                      User ID
                    </Typography>
                    <Typography
                      fontFamily='geistMono'
                      fontSize='labelSm'
                      className='text-tokens-neutral-text-primary'
                    >
                      #{details.profile.user_id}
                    </Typography>
                  </div>

                  <div>
                    <Typography
                      fontFamily='geist'
                      fontSize='labelSm'
                      className='text-tokens-neutral-text-tertiary uppercase tracking-wider mb-0.5'
                    >
                      Phone
                    </Typography>
                    <Typography
                      fontFamily='geist'
                      fontSize='labelSm'
                      className='text-tokens-neutral-text-primary'
                    >
                      {details.verification.phone_number || 'Not set'}
                    </Typography>
                  </div>

                  <div>
                    <Typography
                      fontFamily='geist'
                      fontSize='labelSm'
                      className='text-tokens-neutral-text-tertiary uppercase tracking-wider mb-0.5'
                    >
                      Joined
                    </Typography>
                    <Typography
                      fontFamily='geist'
                      fontSize='labelSm'
                      className='text-tokens-neutral-text-primary'
                    >
                      {details.profile.created_time
                        ? new Date(details.profile.created_time).toLocaleDateString('en-US', {
                            month: 'short',
                            day: 'numeric',
                            year: 'numeric'
                          })
                        : 'N/A'}
                    </Typography>
                  </div>

                  <div>
                    <Typography
                      fontFamily='geist'
                      fontSize='labelSm'
                      className='text-tokens-neutral-text-tertiary uppercase tracking-wider mb-0.5'
                    >
                      Active Sessions
                    </Typography>
                    <Typography
                      fontFamily='geist'
                      fontSize='labelSm'
                      fontWeight='medium'
                      className='text-tokens-neutral-text-primary'
                    >
                      {details.verification.active_sessions_count}
                    </Typography>
                  </div>
                </div>
              </div>

              {/* Main Content Area */}
              <div className='flex-1 flex flex-col min-w-0 overflow-hidden'>
                {/* Tabs */}
                <Tabs
                  selectedKey={activeTab}
                  onSelectionChange={key => setActiveTab(key as string)}
                  className='pl-6 pr-14 pt-4 flex-shrink-0 border-b border-tokens-neutral-border-2'
                  classNames={{
                    base: 'w-full',
                    tabList: 'gap-6 w-full',
                    tab: 'h-10 px-0',
                    panel: 'flex-1 overflow-hidden p-0'
                  }}
                >
                  <Tab key='overview' title='Overview'>
                    <div className='p-6 overflow-y-auto h-full'>
                      {/* Activity Stats */}
                      <div className='mb-6'>
                        <Typography
                          fontFamily='geist'
                          fontSize='labelMd'
                          fontWeight='medium'
                          className='text-tokens-neutral-text-secondary uppercase tracking-wider mb-4'
                        >
                          Activity Summary
                        </Typography>
                        <div className='grid grid-cols-2 lg:grid-cols-4 gap-3'>
                          <div className='bg-gradient-to-br from-tokens-primary-2/10 to-tokens-primary-1/5 rounded-xl p-4 border border-tokens-primary-2/20'>
                            <Typography
                              fontFamily='geist'
                              fontSize='headlineLg'
                              fontWeight='bold'
                              className='text-tokens-primary-2'
                            >
                              {details.activity.total_tasks}
                            </Typography>
                            <Typography
                              fontFamily='geist'
                              fontSize='labelSm'
                              className='text-tokens-neutral-text-secondary mt-1'
                            >
                              Total Tasks
                            </Typography>
                          </div>
                          <div className='bg-tokens-neutral-bg-white rounded-xl p-4 border border-tokens-neutral-border-2'>
                            <Typography
                              fontFamily='geist'
                              fontSize='headlineLg'
                              fontWeight='bold'
                              className='text-tokens-neutral-text-primary'
                            >
                              {details.activity.tasks_last_30_days}
                            </Typography>
                            <Typography
                              fontFamily='geist'
                              fontSize='labelSm'
                              className='text-tokens-neutral-text-secondary mt-1'
                            >
                              Tasks (30d)
                            </Typography>
                          </div>
                          <div className='bg-tokens-neutral-bg-white rounded-xl p-4 border border-tokens-neutral-border-2'>
                            <Typography
                              fontFamily='geist'
                              fontSize='headlineLg'
                              fontWeight='bold'
                              className='text-tokens-neutral-text-primary'
                            >
                              {details.activity.credits_used_last_30_days}
                            </Typography>
                            <Typography
                              fontFamily='geist'
                              fontSize='labelSm'
                              className='text-tokens-neutral-text-secondary mt-1'
                            >
                              Credits (30d)
                            </Typography>
                          </div>
                          <div className='bg-tokens-neutral-bg-white rounded-xl p-4 border border-tokens-neutral-border-2'>
                            <Typography
                              fontFamily='geist'
                              fontSize='bodyMd'
                              fontWeight='semibold'
                              className='text-tokens-neutral-text-primary'
                            >
                              {details.activity.last_task_date
                                ? new Date(details.activity.last_task_date).toLocaleDateString(
                                    'en-US',
                                    {
                                      month: 'short',
                                      day: 'numeric',
                                      year: 'numeric'
                                    }
                                  )
                                : 'Never'}
                            </Typography>
                            <Typography
                              fontFamily='geist'
                              fontSize='labelSm'
                              className='text-tokens-neutral-text-secondary mt-1'
                            >
                              Last Activity
                            </Typography>
                          </div>
                        </div>
                      </div>

                      {/* Quota & Membership */}
                      <div className='grid grid-cols-1 lg:grid-cols-2 gap-4'>
                        {/* Quota Card */}
                        {details.quota && (
                          <div className='bg-tokens-neutral-bg-white rounded-xl p-5 border border-tokens-neutral-border-2'>
                            <Typography
                              fontFamily='geist'
                              fontSize='labelMd'
                              fontWeight='medium'
                              className='text-tokens-neutral-text-secondary uppercase tracking-wider mb-3'
                            >
                              Usage & Quota
                            </Typography>
                            <div className='flex items-center justify-between mb-3'>
                              <div>
                                <Typography
                                  fontFamily='geist'
                                  fontSize='bodyMd'
                                  fontWeight='semibold'
                                  className='text-tokens-neutral-text-primary'
                                >
                                  {details.quota.account_name}
                                </Typography>
                                <Typography
                                  fontFamily='geist'
                                  fontSize='labelSm'
                                  className='text-tokens-neutral-text-tertiary'
                                >
                                  {details.quota.license_name}
                                  {details.quota.is_free_license && ' (Flex)'}
                                </Typography>
                              </div>
                              <div className='text-right'>
                                <Typography
                                  fontFamily='geist'
                                  fontSize='titleMd'
                                  fontWeight='bold'
                                  className={
                                    details.quota.utilization_percent >= 100
                                      ? 'text-tokens-status-error-1'
                                      : details.quota.utilization_percent >= 80
                                        ? 'text-tokens-status-warning-1'
                                        : 'text-tokens-status-success-1'
                                  }
                                >
                                  {details.quota.credits_used}
                                </Typography>
                                <Typography
                                  fontFamily='geist'
                                  fontSize='labelSm'
                                  className='text-tokens-neutral-text-tertiary'
                                >
                                  of {details.quota.credits_limit}
                                </Typography>
                              </div>
                            </div>
                            <Progress
                              value={Math.min(details.quota.utilization_percent, 100)}
                              color={
                                details.quota.utilization_percent >= 100
                                  ? 'danger'
                                  : details.quota.utilization_percent >= 80
                                    ? 'warning'
                                    : 'success'
                              }
                              size='sm'
                              classNames={{
                                track: 'bg-tokens-neutral-bg-grey-2'
                              }}
                            />
                            <Typography
                              fontFamily='geist'
                              fontSize='labelSm'
                              className='text-tokens-neutral-text-tertiary mt-2'
                            >
                              {details.quota.credits_remaining} remaining (
                              {Math.round(details.quota.utilization_percent)}% used)
                            </Typography>
                          </div>
                        )}

                        {/* Membership Summary */}
                        <div className='bg-tokens-neutral-bg-white rounded-xl p-5 border border-tokens-neutral-border-2'>
                          <Typography
                            fontFamily='geist'
                            fontSize='labelMd'
                            fontWeight='medium'
                            className='text-tokens-neutral-text-secondary uppercase tracking-wider mb-3'
                          >
                            Membership
                          </Typography>
                          <div className='space-y-3'>
                            <div className='flex items-center justify-between'>
                              <Typography
                                fontFamily='geist'
                                fontSize='bodySm'
                                className='text-tokens-neutral-text-secondary'
                              >
                                Workspaces
                              </Typography>
                              <Typography
                                fontFamily='geist'
                                fontSize='bodyMd'
                                fontWeight='semibold'
                                className='text-tokens-neutral-text-primary'
                              >
                                {details.workspaces.length}
                              </Typography>
                            </div>
                            <div className='flex items-center justify-between'>
                              <Typography
                                fontFamily='geist'
                                fontSize='bodySm'
                                className='text-tokens-neutral-text-secondary'
                              >
                                Billing Accounts
                              </Typography>
                              <Typography
                                fontFamily='geist'
                                fontSize='bodyMd'
                                fontWeight='semibold'
                                className='text-tokens-neutral-text-primary'
                              >
                                {details.billing_accounts.length}
                              </Typography>
                            </div>
                            <div className='flex items-center justify-between'>
                              <Typography
                                fontFamily='geist'
                                fontSize='bodySm'
                                className='text-tokens-neutral-text-secondary'
                              >
                                Admin Roles
                              </Typography>
                              <Typography
                                fontFamily='geist'
                                fontSize='bodyMd'
                                fontWeight='semibold'
                                className='text-tokens-neutral-text-primary'
                              >
                                {details.billing_accounts.filter(ba => ba.is_account_admin).length}
                              </Typography>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </Tab>

                  <Tab key='workspaces' title={`Workspaces (${details.workspaces.length})`}>
                    <div className='p-6 overflow-y-auto h-full'>
                      {details.workspaces.length === 0 ? (
                        <div className='text-center py-16'>
                          <Typography
                            fontFamily='geist'
                            fontSize='bodyMd'
                            className='text-tokens-neutral-text-tertiary'
                          >
                            No workspaces found
                          </Typography>
                        </div>
                      ) : (
                        <div className='space-y-3'>
                          {details.workspaces.map(ws => (
                            <div
                              key={ws.org_id}
                              className='flex justify-between items-center p-5 rounded-2xl bg-tokens-neutral-bg-white border border-tokens-neutral-border-2 hover:border-tokens-neutral-border-1 transition-colors'
                            >
                              <div className='flex-1 min-w-0'>
                                <div className='flex items-center gap-3'>
                                  <Typography
                                    fontFamily='geist'
                                    fontSize='bodyMd'
                                    fontWeight='semibold'
                                    className='text-tokens-neutral-text-primary'
                                  >
                                    {ws.org_name}
                                  </Typography>
                                  {ws.is_personal && (
                                    <Chip size='sm' variant='flat' color='secondary'>
                                      Personal
                                    </Chip>
                                  )}
                                </div>
                                <div className='flex items-center gap-2 mt-2'>
                                  <Typography
                                    fontFamily='geistMono'
                                    fontSize='labelSm'
                                    className='text-tokens-neutral-text-tertiary truncate'
                                  >
                                    {ws.workspace_uuid}
                                  </Typography>
                                  <button
                                    onClick={() => copyToClipboard(ws.workspace_uuid)}
                                    className='text-tokens-neutral-text-quaternary hover:text-tokens-neutral-text-secondary transition-colors flex-shrink-0'
                                  >
                                    <CopyIcon />
                                  </button>
                                </div>
                              </div>
                              <Chip
                                size='sm'
                                variant='flat'
                                color='primary'
                                classNames={{ content: 'capitalize font-medium' }}
                              >
                                {ws.role}
                              </Chip>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </Tab>

                  <Tab key='billing' title={`Billing (${details.billing_accounts.length})`}>
                    <div className='p-6 overflow-y-auto h-full'>
                      {details.billing_accounts.length === 0 ? (
                        <div className='text-center py-16'>
                          <Typography
                            fontFamily='geist'
                            fontSize='bodyMd'
                            className='text-tokens-neutral-text-tertiary'
                          >
                            No billing accounts found
                          </Typography>
                        </div>
                      ) : (
                        <div className='space-y-3'>
                          {details.billing_accounts.map(ba => (
                            <div
                              key={ba.billing_id}
                              className='flex justify-between items-center p-5 rounded-2xl bg-tokens-neutral-bg-white border border-tokens-neutral-border-2 hover:border-tokens-neutral-border-1 transition-colors'
                            >
                              <div>
                                <Typography
                                  fontFamily='geist'
                                  fontSize='bodyMd'
                                  fontWeight='semibold'
                                  className='text-tokens-neutral-text-primary'
                                >
                                  {ba.account_name}
                                </Typography>
                                <Typography
                                  fontFamily='geistMono'
                                  fontSize='labelSm'
                                  className='text-tokens-neutral-text-tertiary mt-1'
                                >
                                  ID #{ba.billing_id}
                                </Typography>
                              </div>
                              <div className='flex items-center gap-2'>
                                {ba.is_account_admin && (
                                  <Chip size='sm' variant='flat' color='warning'>
                                    Admin
                                  </Chip>
                                )}
                                <Chip
                                  size='sm'
                                  variant='flat'
                                  color='primary'
                                  classNames={{ content: 'capitalize font-medium' }}
                                >
                                  {ba.role}
                                </Chip>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </Tab>

                  <Tab key='tasks' title='Tasks'>
                    <div className='p-6 overflow-y-auto h-full'>
                      {isLoadingTasks ? (
                        <div className='flex justify-center py-16'>
                          <Spinner size='lg' />
                        </div>
                      ) : tasks && tasks.tasks.length > 0 ? (
                        <div className='space-y-3'>
                          {tasks.tasks.map(task => (
                            <div
                              key={task.task_id}
                              className='flex justify-between items-center p-5 rounded-2xl bg-tokens-neutral-bg-white border border-tokens-neutral-border-2 hover:border-tokens-neutral-border-1 transition-colors'
                            >
                              <div className='flex items-center gap-4'>
                                <div
                                  className={`w-10 h-10 rounded-xl flex items-center justify-center ${
                                    task.status === 'COMPLETED'
                                      ? 'bg-tokens-status-success-3'
                                      : task.status === 'ERRORED'
                                        ? 'bg-tokens-status-error-3'
                                        : 'bg-tokens-status-warning-3'
                                  }`}
                                >
                                  {task.status === 'COMPLETED' ? (
                                    <CheckCircleIcon />
                                  ) : task.status === 'ERRORED' ? (
                                    <ExclamationCircleIcon />
                                  ) : (
                                    <svg
                                      className='w-4 h-4 animate-spin'
                                      fill='none'
                                      viewBox='0 0 24 24'
                                    >
                                      <circle
                                        className='opacity-25'
                                        cx='12'
                                        cy='12'
                                        r='10'
                                        stroke='currentColor'
                                        strokeWidth='4'
                                      />
                                      <path
                                        className='opacity-75'
                                        fill='currentColor'
                                        d='M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z'
                                      />
                                    </svg>
                                  )}
                                </div>
                                <div>
                                  <div className='flex items-center gap-3'>
                                    <Typography
                                      fontFamily='geistMono'
                                      fontSize='bodyMd'
                                      fontWeight='semibold'
                                      className='text-tokens-neutral-text-primary'
                                    >
                                      #{task.task_id}
                                    </Typography>
                                    <Chip size='sm' variant='flat'>
                                      {task.cmd_type}
                                    </Chip>
                                  </div>
                                  <Typography
                                    fontFamily='geist'
                                    fontSize='bodySm'
                                    className='text-tokens-neutral-text-tertiary mt-1'
                                  >
                                    {task.created_time
                                      ? new Date(task.created_time).toLocaleString('en-US', {
                                          month: 'short',
                                          day: 'numeric',
                                          year: 'numeric',
                                          hour: '2-digit',
                                          minute: '2-digit'
                                        })
                                      : 'N/A'}
                                  </Typography>
                                </div>
                              </div>
                              <Chip
                                size='sm'
                                variant='flat'
                                color={
                                  task.status === 'COMPLETED'
                                    ? 'success'
                                    : task.status === 'ERRORED'
                                      ? 'danger'
                                      : 'warning'
                                }
                                classNames={{ content: 'font-medium' }}
                              >
                                {task.status}
                              </Chip>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className='text-center py-16'>
                          <Typography
                            fontFamily='geist'
                            fontSize='bodyMd'
                            className='text-tokens-neutral-text-tertiary'
                          >
                            No tasks found
                          </Typography>
                        </div>
                      )}
                    </div>
                  </Tab>

                  <Tab key='access-logs' title='Logs'>
                    <div className='p-6 overflow-y-auto h-full'>
                      <div className='flex items-center justify-between mb-6'>
                        <div className='flex items-center gap-3'>
                          <Typography
                            fontFamily='geist'
                            fontSize='bodySm'
                            fontWeight='medium'
                            className='text-tokens-neutral-text-secondary'
                          >
                            Date Range:
                          </Typography>
                          <DateRangePicker
                            // eslint-disable-next-line @typescript-eslint/no-explicit-any
                            value={logDateRange as any}
                            // eslint-disable-next-line @typescript-eslint/no-explicit-any
                            onChange={handleDateRangeChange as any}
                            size='sm'
                            className='w-64'
                          />
                        </div>
                        <Button
                          size='sm'
                          variant='flat'
                          onClick={() => {
                            setAccessLogs(null)
                            fetchAccessLogs()
                          }}
                          isLoading={isLoadingLogs}
                        >
                          Refresh
                        </Button>
                      </div>

                      {isLoadingLogs ? (
                        <div className='flex justify-center py-12'>
                          <Spinner size='lg' />
                        </div>
                      ) : accessLogs && accessLogs.length > 0 ? (
                        <div className='border border-tokens-neutral-border-2 rounded-xl overflow-hidden'>
                          <div className='overflow-auto max-h-96'>
                            <table className='w-full'>
                              <thead className='bg-tokens-neutral-bg-grey-2 sticky top-0'>
                                <tr>
                                  <th className='px-4 py-3 text-left'>
                                    <Typography
                                      fontFamily='geist'
                                      fontSize='labelSm'
                                      fontWeight='medium'
                                      className='text-tokens-neutral-text-secondary'
                                    >
                                      Timestamp
                                    </Typography>
                                  </th>
                                  <th className='px-4 py-3 text-left'>
                                    <Typography
                                      fontFamily='geist'
                                      fontSize='labelSm'
                                      fontWeight='medium'
                                      className='text-tokens-neutral-text-secondary'
                                    >
                                      Action
                                    </Typography>
                                  </th>
                                  <th className='px-4 py-3 text-left'>
                                    <Typography
                                      fontFamily='geist'
                                      fontSize='labelSm'
                                      fontWeight='medium'
                                      className='text-tokens-neutral-text-secondary'
                                    >
                                      IP Address
                                    </Typography>
                                  </th>
                                </tr>
                              </thead>
                              <tbody className='divide-y divide-tokens-neutral-border-2'>
                                {accessLogs.map((log, idx) => (
                                  <tr
                                    key={idx}
                                    className='hover:bg-tokens-neutral-fill-fill-2 transition-colors'
                                  >
                                    <td className='px-4 py-3 whitespace-nowrap'>
                                      <Typography
                                        fontFamily='geistMono'
                                        fontSize='labelSm'
                                        className='text-tokens-neutral-text-primary'
                                      >
                                        {log.timestamp || 'N/A'}
                                      </Typography>
                                    </td>
                                    <td className='px-4 py-3'>
                                      <Chip size='sm' variant='flat'>
                                        {log.action || 'Unknown'}
                                      </Chip>
                                    </td>
                                    <td className='px-4 py-3'>
                                      <Typography
                                        fontFamily='geistMono'
                                        fontSize='labelSm'
                                        className='text-tokens-neutral-text-primary'
                                      >
                                        {log.ip_address || 'N/A'}
                                      </Typography>
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      ) : (
                        <div className='text-center py-12'>
                          <Typography
                            fontFamily='geist'
                            fontSize='bodyMd'
                            className='text-tokens-neutral-text-tertiary'
                          >
                            No access logs found for this date range
                          </Typography>
                        </div>
                      )}
                    </div>
                  </Tab>

                  <Tab key='actions' title='Actions'>
                    <div className='p-6 space-y-5 overflow-y-auto h-full'>
                      {/* Verification Settings */}
                      <div className='bg-tokens-neutral-bg-white rounded-2xl p-6 border border-tokens-neutral-border-2'>
                        <Typography
                          fontFamily='geist'
                          fontSize='titleSm'
                          fontWeight='semibold'
                          className='text-tokens-neutral-text-primary mb-4'
                        >
                          Verification Settings
                        </Typography>
                        <div className='space-y-3'>
                          <div className='flex items-center justify-between p-4 bg-tokens-neutral-bg-grey-1 rounded-xl'>
                            <div>
                              <Typography
                                fontFamily='geist'
                                fontSize='bodyMd'
                                fontWeight='medium'
                                className='text-tokens-neutral-text-primary'
                              >
                                Email Verification
                              </Typography>
                              <Typography
                                fontFamily='geist'
                                fontSize='labelSm'
                                className='text-tokens-neutral-text-tertiary mt-0.5'
                              >
                                {details.verification.email_verified
                                  ? 'Email is verified'
                                  : 'Email is not verified'}
                              </Typography>
                            </div>
                            <Button
                              size='sm'
                              color={details.verification.email_verified ? 'danger' : 'success'}
                              variant='flat'
                              onClick={handleToggleEmailVerified}
                              isLoading={actionLoading === 'email'}
                            >
                              {details.verification.email_verified ? 'Unverify' : 'Verify'}
                            </Button>
                          </div>

                          <div className='flex items-center justify-between p-4 bg-tokens-neutral-bg-grey-1 rounded-xl'>
                            <div>
                              <Typography
                                fontFamily='geist'
                                fontSize='bodyMd'
                                fontWeight='medium'
                                className='text-tokens-neutral-text-primary'
                              >
                                Phone Verification
                              </Typography>
                              <Typography
                                fontFamily='geist'
                                fontSize='labelSm'
                                className='text-tokens-neutral-text-tertiary mt-0.5'
                              >
                                {details.verification.phone_number
                                  ? details.verification.phone_verified
                                    ? 'Phone is verified'
                                    : 'Phone is not verified'
                                  : 'No phone number set'}
                              </Typography>
                            </div>
                            <Button
                              size='sm'
                              color={details.verification.phone_verified ? 'danger' : 'success'}
                              variant='flat'
                              onClick={handleTogglePhoneVerified}
                              isLoading={actionLoading === 'phone'}
                              isDisabled={!details.verification.phone_number}
                            >
                              {details.verification.phone_verified ? 'Unverify' : 'Verify'}
                            </Button>
                          </div>

                          <div className='flex items-center justify-between p-4 bg-tokens-neutral-bg-grey-1 rounded-xl'>
                            <div>
                              <Typography
                                fontFamily='geist'
                                fontSize='bodyMd'
                                fontWeight='medium'
                                className='text-tokens-neutral-text-primary'
                              >
                                MFA Status
                              </Typography>
                              <Typography
                                fontFamily='geist'
                                fontSize='labelSm'
                                className='text-tokens-neutral-text-tertiary mt-0.5'
                              >
                                {details.verification.mfa_enabled
                                  ? 'MFA is enabled'
                                  : 'MFA is disabled'}
                              </Typography>
                            </div>
                            <Button
                              size='sm'
                              color={details.verification.mfa_enabled ? 'danger' : 'success'}
                              variant='flat'
                              onClick={handleToggleMfa}
                              isLoading={actionLoading === 'mfa'}
                            >
                              {details.verification.mfa_enabled ? 'Disable' : 'Enable'}
                            </Button>
                          </div>
                        </div>
                      </div>

                      {/* Security Actions */}
                      <div className='bg-tokens-neutral-bg-white rounded-2xl p-6 border border-tokens-neutral-border-2'>
                        <Typography
                          fontFamily='geist'
                          fontSize='titleSm'
                          fontWeight='semibold'
                          className='text-tokens-neutral-text-primary mb-4'
                        >
                          Security Actions
                        </Typography>
                        <div className='space-y-3'>
                          <div className='flex items-center justify-between p-4 bg-tokens-neutral-bg-grey-1 rounded-xl'>
                            <div>
                              <Typography
                                fontFamily='geist'
                                fontSize='bodyMd'
                                fontWeight='medium'
                                className='text-tokens-neutral-text-primary'
                              >
                                Revoke All Sessions
                              </Typography>
                              <Typography
                                fontFamily='geist'
                                fontSize='labelSm'
                                className='text-tokens-neutral-text-tertiary mt-0.5'
                              >
                                Force logout from all devices (
                                {details.verification.active_sessions_count} active)
                              </Typography>
                            </div>
                            <Button
                              size='sm'
                              color='danger'
                              variant='flat'
                              onClick={handleRevokeSessions}
                              isLoading={actionLoading === 'revoke'}
                              isDisabled={details.verification.active_sessions_count === 0}
                            >
                              Revoke Sessions
                            </Button>
                          </div>

                          {!details.verification.email_verified && (
                            <div className='flex items-center justify-between p-4 bg-tokens-neutral-bg-grey-1 rounded-xl'>
                              <div>
                                <Typography
                                  fontFamily='geist'
                                  fontSize='bodyMd'
                                  fontWeight='medium'
                                  className='text-tokens-neutral-text-primary'
                                >
                                  Resend Verification Email
                                </Typography>
                                <Typography
                                  fontFamily='geist'
                                  fontSize='labelSm'
                                  className='text-tokens-neutral-text-tertiary mt-0.5'
                                >
                                  Send a new email verification link
                                </Typography>
                              </div>
                              <Button
                                size='sm'
                                color='primary'
                                variant='flat'
                                onClick={handleResendVerification}
                                isLoading={actionLoading === 'resend'}
                              >
                                Send Email
                              </Button>
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Account Lock Status */}
                      <div className='bg-tokens-neutral-bg-white rounded-2xl p-6 border border-tokens-neutral-border-2'>
                        <Typography
                          fontFamily='geist'
                          fontSize='titleSm'
                          fontWeight='semibold'
                          className='text-tokens-neutral-text-primary mb-4'
                        >
                          Account Lock Status
                        </Typography>
                        <div className='space-y-3'>
                          <div
                            className={`flex items-center justify-between p-4 rounded-xl ${
                              details.verification.unsuccessful_login_attempts >
                              ACCOUNT_LOCK_THRESHOLD
                                ? 'bg-tokens-status-error-3 border border-tokens-status-error-2'
                                : 'bg-tokens-neutral-bg-grey-1'
                            }`}
                          >
                            <div>
                              <Typography
                                fontFamily='geist'
                                fontSize='bodyMd'
                                fontWeight='medium'
                                className='text-tokens-neutral-text-primary'
                              >
                                {details.verification.unsuccessful_login_attempts >
                                ACCOUNT_LOCK_THRESHOLD
                                  ? 'Account Locked'
                                  : 'Account Active'}
                              </Typography>
                              <Typography
                                fontFamily='geist'
                                fontSize='labelSm'
                                className='text-tokens-neutral-text-tertiary mt-0.5'
                              >
                                {details.verification.unsuccessful_login_attempts > 0
                                  ? `${details.verification.unsuccessful_login_attempts} failed login attempt${details.verification.unsuccessful_login_attempts !== 1 ? 's' : ''} (locks after ${ACCOUNT_LOCK_THRESHOLD})`
                                  : 'No failed login attempts'}
                              </Typography>
                            </div>
                            <div className='flex items-center gap-2'>
                              <Chip
                                color={
                                  details.verification.unsuccessful_login_attempts >
                                  ACCOUNT_LOCK_THRESHOLD
                                    ? 'danger'
                                    : 'success'
                                }
                                variant='flat'
                                size='sm'
                              >
                                {details.verification.unsuccessful_login_attempts >
                                ACCOUNT_LOCK_THRESHOLD
                                  ? 'Locked'
                                  : 'Active'}
                              </Chip>
                              {details.verification.unsuccessful_login_attempts >
                                ACCOUNT_LOCK_THRESHOLD && (
                                <Button
                                  size='sm'
                                  color='warning'
                                  variant='flat'
                                  onClick={handleUnlockAccount}
                                  isLoading={actionLoading === 'unlock'}
                                >
                                  Unlock Account
                                </Button>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Warning */}
                      <div className='p-5 bg-tokens-status-warning-3 border border-tokens-status-warning-2 rounded-2xl'>
                        <Typography
                          fontFamily='geist'
                          fontSize='bodySm'
                          className='text-tokens-status-warning-1'
                        >
                          <strong>Warning:</strong> These actions bypass normal user flows and
                          should only be used for support/debugging purposes.
                        </Typography>
                      </div>
                    </div>
                  </Tab>
                </Tabs>
              </div>
            </div>
          ) : (
            <div className='text-center py-20'>
              <Typography
                fontFamily='geist'
                fontSize='bodyMd'
                className='text-tokens-neutral-text-tertiary'
              >
                Failed to load user details
              </Typography>
            </div>
          )}
        </ModalBody>
      </Modal>

      {/* Confirmation Modal */}
      <Modal
        isOpen={confirmModal.isOpen}
        onClose={confirmModal.close}
        hideCloseButton={false}
        className='max-w-md'
      >
        <ModalBody>
          <Typography
            fontFamily='geist'
            fontSize='titleMd'
            fontWeight='semibold'
            className='text-tokens-neutral-text-primary'
          >
            {confirmAction?.title}
          </Typography>
          <Typography
            fontFamily='geist'
            fontSize='bodyMd'
            className='text-tokens-neutral-text-secondary mt-2'
          >
            {confirmAction?.message}
          </Typography>
        </ModalBody>
        <ModalFooter>
          <Button variant='light' onClick={confirmModal.close}>
            Cancel
          </Button>
          <Button color='primary' onClick={executeConfirmAction}>
            Confirm
          </Button>
        </ModalFooter>
      </Modal>
    </>
  )
}

export default UserDetails
